import type { Optional } from '@festina-life/core'

import { useLayoutContext } from '../../context/layoutContext'
import { useTemplateContext } from '../../context/templateContext'
import { useTemplateIsLibrary } from '../../hooks/useTemplateIsLibrary'
import type { FPlusExpressionValue } from '../../types/templateModel'
import type { WithOptionalOriginInfo } from '../../uiModels/types'
import { FPlusExpressionEditor } from '../fplusExpression/FPlusExpressionEditor'
import { FPlusExpressionViewer } from '../fplusExpression/FPlusExpressionViewer'
import type {
  ExpressionTypeInformation,
  FPlusExpressionProps,
} from '../fplusExpression/types'
import { useFieldPathOnCurrentTemplate } from './useFieldPathOnCurrentTemplate'

type Props = {
  expression: Optional<FPlusExpressionValue>
  path: string
  label?: string
  typeInformation?: Optional<ExpressionTypeInformation>
  isShowingOverflowFallback: boolean
  onChange?: Optional<
    (nextValue: WithOptionalOriginInfo<FPlusExpressionValue>) => Promise<void>
  >
  isEditable: boolean
  fallbackCode: string
  overflowTitle?: Optional<string>
  isExpanded?: boolean
}

export const TemplateFieldFPlusExpression = ({
  isExpanded,
  expression,
  isShowingOverflowFallback,
  path,
  label,
  typeInformation,
  onChange,
  isEditable,
  fallbackCode,
  overflowTitle,
}: Props) => {
  const { templateVersion } = useTemplateContext()
  const editingPath = useFieldPathOnCurrentTemplate(path)
  const isLibrary = useTemplateIsLibrary()
  const {
    preferences: {
      templates: { isShowingSimplifiedView },
    },
  } = useLayoutContext()

  const expressionProps: FPlusExpressionProps = {
    typeInformation,
    isTransparent: !isExpanded,
    expression,
    path: editingPath,
    label,
    shouldShowOverflowFallback: isShowingOverflowFallback,
    type:
      templateVersion && isLibrary(templateVersion.versionInfo.identifier)
        ? 'library'
        : 'template',
  }

  return isEditable && onChange ? (
    <FPlusExpressionEditor
      key={`${path}-${isShowingSimplifiedView}`}
      {...expressionProps}
      fallbackCode={fallbackCode}
      onChange={onChange}
    />
  ) : (
    <FPlusExpressionViewer
      key={`${path}-${isShowingSimplifiedView}`}
      isShowingFunctionDeclaration={false}
      alignment={isExpanded || isShowingSimplifiedView ? 'left' : 'center'}
      overflowTitle={overflowTitle}
      {...expressionProps}
    />
  )
}
